// src/components/ScreenCapture.jsx
import React, { useLayoutEffect, useRef, useState } from 'react';

const CONTENT_BASE_W = 1920;
const CONTENT_BASE_H = 1080;

// BattleAnalyzer의 getFrame과 동일
function getFrame(videoEl) {
  if (!videoEl || !videoEl.videoWidth || !videoEl.videoHeight) {
    return { LEFT: 1, RIGHT: 1, BOTTOM: 0, TOP: 0 };
  }
  const LEFT = 1;
  const RIGHT = 1;
  const BOTTOM = 0;
  const contentH = ((videoEl.videoWidth - (LEFT + RIGHT)) / 16) * 9;
  const TOP = Math.max(0, videoEl.videoHeight - contentH - BOTTOM);
  return { LEFT, RIGHT, BOTTOM, TOP };
}

export default function ScreenCapture({
  videoRef,
  overlayRef,
  width = '100%',
  viewROI = null,   // {x,y,w,h} in 1920x1080 coords
  zoom = 1
}) {
  const wrapRef = useRef(null);
  const groupRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [groupStyle, setGroupStyle] = useState({ transform: 'none' });

  // 비디오 준비 상태
  React.useEffect(() => {
    const v = videoRef?.current;
    if (!v) return;

    const updateReady = () => setReady(Boolean(v.srcObject || v.src));
    const setReadyTrue = () => setReady(true);

    updateReady();

    v.addEventListener('loadedmetadata', setReadyTrue);
    v.addEventListener('loadeddata', setReadyTrue);
    v.addEventListener('canplay', setReadyTrue);
    v.addEventListener('play', setReadyTrue);

    const onEmpty = () => setReady(Boolean(v.srcObject || v.src));
    v.addEventListener('emptied', onEmpty);
    v.addEventListener('abort', onEmpty);
    v.addEventListener('ended', onEmpty);

    return () => {
      v.removeEventListener('loadedmetadata', setReadyTrue);
      v.removeEventListener('loadeddata', setReadyTrue);
      v.removeEventListener('canplay', setReadyTrue);
      v.removeEventListener('play', setReadyTrue);
      v.removeEventListener('emptied', onEmpty);
      v.removeEventListener('abort', onEmpty);
      v.removeEventListener('ended', onEmpty);
    };
  }, [videoRef]);

  // ROI 뷰 transform 계산 (레이아웃 단계에서)
  useLayoutEffect(() => {
    const v = videoRef?.current;
    const wrap = wrapRef.current;
    if (!v || !wrap) return;

    function recalc() {
      // 메타 없으면 원위치
      if (!v.videoWidth || !v.videoHeight || !wrap.clientWidth || !wrap.clientHeight) {
        setGroupStyle({ transform: 'none' });
        // 캔버스도 초기화
        if (overlayRef?.current) {
          const c = overlayRef.current;
          if (c.width !== 1 || c.height !== 1) { c.width = 1; c.height = 1; }
        }
        return;
      }

      // 컨테이너 크기 (aspect-video 역할)
      const CW = wrap.clientWidth;
      const CH = wrap.clientHeight;

      // 실제 그려진 비디오 표면 크기: 우선 clientWidth/Height 사용
      // (브라우저별 객체맞춤 차이를 흡수)
      let vwDraw = v.clientWidth;
      let vhDraw = v.clientHeight;

      // 혹시 0이면 컨테이너 비율 기반으로 폴백 계산
      if (!vwDraw || !vhDraw) {
        const vRatio = v.videoWidth / v.videoHeight;
        const cRatio = CW / CH;
        if (vRatio > cRatio) {
          vwDraw = CW;
          vhDraw = Math.round(CW / vRatio);
        } else {
          vhDraw = CH;
          vwDraw = Math.round(CH * vRatio);
        }
      }

      // 오버레이 표면 크기를 항상 동기화
      if (overlayRef?.current) {
        const c = overlayRef.current;
        if (c.width !== vwDraw || c.height !== vhDraw) {
          c.width = vwDraw;
          c.height = vhDraw;
        }
      }

      // ROI가 없으면 전체 보기
      if (!viewROI) {
        setGroupStyle({
          width: vwDraw,
          height: vhDraw,
          transformOrigin: 'top left',
          transform: 'translate(0px, 0px) scale(1)'
        });
        return;
      }

      // 컨텐츠 → 비디오 픽셀 스케일
      const FRAME = getFrame(v);
      const cw = Math.max(0, v.videoWidth - (FRAME.LEFT + FRAME.RIGHT));
      const ch = Math.max(0, v.videoHeight - (FRAME.TOP + FRAME.BOTTOM));
      const scaleX = cw / CONTENT_BASE_W;
      const scaleY = ch / CONTENT_BASE_H;

      // 비디오 픽셀 → 표면 픽셀 스케일
      const ex = vwDraw / v.videoWidth;
      const ey = vhDraw / v.videoHeight;

      // 컨텐츠 ROI → 표면 픽셀
      const sx_v = FRAME.LEFT + viewROI.x * scaleX;
      const sy_v = FRAME.TOP  + viewROI.y * scaleY;
      const sw_v = viewROI.w * scaleX;
      const sh_v = viewROI.h * scaleY;

      const sx_el = sx_v * ex;
      const sy_el = sy_v * ey;
      const sw_el = sw_v * ex;
      const sh_el = sh_v * ey;

      // ROI fit + 추가 배율
      const fitK = Math.min(CW / sw_el, CH / sh_el);
      const k = fitK * (zoom > 0 ? zoom : 1);

      // ROI 좌상단을 (0,0)으로 이동 후 중앙정렬
      const dxToZero = -sx_el * k;
      const dyToZero = -sy_el * k;
      const cxOffset = (CW - sw_el * k) / 2;
      const cyOffset = (CH - sh_el * k) / 2;

      setGroupStyle({
        width: vwDraw,
        height: vhDraw,
        transformOrigin: 'top left',
        transform: `translate(${dxToZero + cxOffset}px, ${dyToZero + cyOffset}px) scale(${k})`
      });
    }

    recalc();

    // 사이즈 변동 대응
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);

    const onMeta = () => recalc();
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('resize', onMeta); // 일부 브라우저에서 비디오 표면 변화 이벤트

    const ro = new ResizeObserver(recalc);
    ro.observe(wrap);

    return () => {
      window.removeEventListener('resize', onResize);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('resize', onMeta);
      ro.disconnect();
    };
  }, [videoRef, overlayRef, viewROI, zoom]);

  // iOS 등 자동재생 보장
  const onMetaPlay = e => e.currentTarget.play().catch(() => {});

  return (
    <div
      id="video-wrap"
      ref={wrapRef}
      className="relative inline-block align-top overflow-hidden bg-black"
      style={{
        width,
        aspectRatio: viewROI ? `${viewROI.w} / ${viewROI.h}` : '16 / 9'
      }}
    >
      {/* 비디오+오버레이를 함께 이동/확대 */}
      <div
        ref={groupRef}
        className="absolute top-0 left-0 will-change-transform"
        style={{ transformOrigin: 'top left', ...groupStyle }}
      >
        <video
          ref={videoRef}
          muted
          autoPlay
          playsInline
          controls={false}
          className="block"
          style={{ width: '100%', height: '100%' }}
          onLoadedMetadata={onMetaPlay}
        />
        <canvas
          ref={overlayRef}
          className="pointer-events-none absolute top-0 left-0"
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {!ready && (
        <div className="absolute inset-0 grid place-items-center">
          <p className="text-white text-sm">화면 공유 시작 버튼을 눌러주세요</p>
        </div>
      )}
    </div>
  );
}
